import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button, TextField } from '@/ui';

import { SavedNoteSnapshotsView } from './SavedNoteSnapshotsView';

import {
  useDeleteMomentsArtifactNoteMutation,
  useGetMomentsApiSessionReadinessQuery,
  useSearchMomentsSavedHighlightsQuery,
  useUpdateMomentsArtifactNoteMutation,
} from '@/store/api/momentsApi';
import { useAppSelector } from '@/store/hooks';

const OwnerSnapshotBody = ({
  cacheSubject,
  noteId,
  ownerPersonId,
}: {
  cacheSubject: string;
  noteId?: string;
  ownerPersonId: string;
}) => {
  const [, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const query = useSearchMomentsSavedHighlightsQuery(
    {
      cacheSubject,
      direction: 'desc',
      limit: 20,
      noteId,
      offset,
      q: search.trim() || undefined,
      sort: 'created_at',
      view: 'owned-snapshots',
    },
    { refetchOnFocus: true, refetchOnReconnect: true },
  );
  const [update, updating] = useUpdateMomentsArtifactNoteMutation();
  const [remove, removing] = useDeleteMomentsArtifactNoteMutation();
  // currentData belongs only to this owner/generation/view/page. Do not reuse
  // earlier pages/subjects while the next query is unresolved.
  const response = query.currentData;
  const ownerMatches = response?.items.every(
    (note) => note.ownerPersonId === ownerPersonId,
  );
  const data = ownerMatches ? response : undefined;
  let status: 'error' | 'ready' | 'loading' = 'loading';
  if (query.isError || (response && !ownerMatches)) {
    status = 'error';
  } else if (data) {
    status = 'ready';
  }
  return (
    <main className="mx-auto max-w-[800px] px-4 py-8">
      <Link to="/m/highlights">Source-visible highlights</Link>
      {noteId ? (
        <Button
          onClick={() => {
            setParams({ view: 'owned-snapshots' });
            setOffset(0);
          }}
        >
          Browse all saved notes
        </Button>
      ) : null}
      <TextField
        inputProps={{ 'aria-label': 'Search saved notes' }}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setOffset(0);
        }}
      />
      {data ? (
        <p>
          {data.totalCount} saved {data.totalCount === 1 ? 'note' : 'notes'} in
          this view
        </p>
      ) : null}
      <SavedNoteSnapshotsView
        busy={updating.isLoading || removing.isLoading}
        notes={data?.items ?? []}
        status={status}
        onDelete={async (id) => {
          await remove({ id }).unwrap();
        }}
        onUpdate={async (id, note) => {
          await update({ id, note }).unwrap();
        }}
      />
      <div className="flex gap-3 py-4">
        <Button
          disabled={offset === 0 || query.isFetching}
          onClick={() => setOffset(Math.max(0, offset - 20))}
        >
          Previous saved notes
        </Button>
        <Button
          disabled={!data?.hasMore || query.isFetching}
          onClick={() => setOffset(data?.nextOffset ?? offset)}
        >
          Next saved notes
        </Button>
      </div>
    </main>
  );
};

// Normal saved-note management deliberately has no discovery/Keep flag query.
// Authentication/member capability and the server's self/owner checks remain.
export const MemberOwnedNoteSnapshotsPage = () => {
  const [params] = useSearchParams();
  const generation = useAppSelector((state) => state.app.momentsGeneration);
  const readinessQuery = useGetMomentsApiSessionReadinessQuery(undefined, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });
  const readiness = readinessQuery.currentData;
  if (
    !readiness?.ready ||
    !readiness.capabilities.memberApi ||
    !Array.isArray(readiness.roles) ||
    !readiness.roles.includes('member') ||
    !readiness.person?.id
  ) {
    return (
      <main className="px-4 py-8">
        <p role="status">
          {readinessQuery.isError || readiness
            ? 'Saved notes are unavailable for this session.'
            : 'Checking your saved notes session…'}
        </p>
      </main>
    );
  }
  const cacheSubject = `${readiness.person.id}:${generation}`;
  return (
    <OwnerSnapshotBody
      key={cacheSubject}
      cacheSubject={cacheSubject}
      noteId={params.get('noteId') ?? undefined}
      ownerPersonId={readiness.person.id}
    />
  );
};
